import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import CodeIcon from "@material-ui/icons/Code";
import SendIcon from "@material-ui/icons/Send";
import DescriptionIcon from "@material-ui/icons/Description";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import PersonIcon from "@material-ui/icons/Person";
import VpnKeyIcon from "@material-ui/icons/VpnKey";

import axios from "axios";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(6),
    overflowY: "auto",
    maxWidth: 1200,
    margin: "0 auto",
    ...theme.scrollbarStyles,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    fontSize: "1.75rem",
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
  },
  card: {
    borderRadius: 12,
    boxShadow: theme.palette.type === "dark"
      ? "0 4px 20px rgba(0,0,0,0.3)"
      : "0 2px 12px rgba(0,0,0,0.08)",
    marginBottom: theme.spacing(3),
    overflow: "hidden",
  },
  cardHeader: {
    paddingBottom: 0,
    "& .MuiCardHeader-title": {
      fontSize: "1.1rem",
      fontWeight: 600,
    },
    "& .MuiCardHeader-subheader": {
      fontSize: "0.85rem",
      marginTop: 4,
    },
  },
  cardContent: {
    padding: theme.spacing(3),
    "&:last-child": { paddingBottom: theme.spacing(3) },
  },
  tabsRoot: {
    minHeight: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    minHeight: 48,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  codeBlock: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.35)" : "#f8f9fa",
    padding: theme.spacing(2),
    borderRadius: 8,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    fontSize: "0.8rem",
    lineHeight: 1.6,
    overflowX: "auto",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
  },
  formGrid: {
    "& .MuiTextField-root": { margin: 0 },
  },
  submitBtn: {
    textTransform: "none",
    fontWeight: 600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  fileInputWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  fileInput: {
    display: "none",
  },
  fileButton: {
    textTransform: "none",
  },
  instructionsList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    "& li": { marginBottom: theme.spacing(0.5) },
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();
  const paperRef = useRef(null);

  const [formMessageTextData] = useState({
    token: "", number: "", body: "", userId: "", queueId: "",
    buttons: "", sections: "", buttonText: "Escolha uma opção",
  });
  const [formMessageMediaData] = useState({
    token: "", number: "", medias: "", body: "", userId: "", queueId: "",
  });
  const [file, setFile] = useState({});
  const [tabModelo, setTabModelo] = useState(0);
  const [tabTicket, setTabTicket] = useState(0);
  const [tabContato, setTabContato] = useState(0);
  const [tabTeste, setTabTeste] = useState(0);
  const [formTextoData] = useState({ token: "", number: "", body: "", userId: "", queueId: "" });
  const [formBotoesData] = useState({
    token: "", number: "", body: "Escolha uma opção:",
    btn1: "", btn2: "", btn3: "", userId: "", queueId: "",
  });
  const [formListaData] = useState({
    token: "", number: "", body: "Selecione uma opção:",
    buttonText: "Escolha uma opção",
    sections: '[{"title":"Opções","rows":[{"id":"1","title":"Opção A"},{"id":"2","title":"Opção B"}]}]',
    userId: "", queueId: "",
  });
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (paperRef.current) paperRef.current.scrollTop = 0;
  }, []);

  useEffect(() => {
    async function fetchData() {
      const companyId = user?.companyId;
      if (!companyId) return;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs?.plan?.useExternalApi) {
        toast.error("Esta empresa não possui permissão para acessar essa página!");
        setTimeout(() => history.push("/"), 1000);
      }
    }
    fetchData();
  }, [user?.companyId, getPlanCompany, history]);

  const getBaseApi = () => `${process.env.REACT_APP_BACKEND_URL}/api/messages`;
  const getEndpoint = () => `${getBaseApi()}/send`;

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId, buttons, sections, buttonText } = values;
    const data = { number, body: body || " ", userId, queueId };
    if (buttonText) data.buttonText = buttonText;
    if (buttons?.trim()) {
      try { data.buttons = JSON.parse(buttons.trim()); } catch (_) {
        toast.error("Botões: JSON inválido."); return;
      }
    }
    if (sections?.trim()) {
      try { data.sections = JSON.parse(sections.trim()); } catch (_) {
        toast.error("Lista (sections): JSON inválido."); return;
      }
    }
    try {
      await axios.request({ url: getEndpoint(), method: "POST", data, headers: { "Content-type": "application/json", Authorization: `Bearer ${values.token}` } });
      toast.success("Mensagem enviada com sucesso");
    } catch (err) { toastError(err); }
  };

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append("number", values.number);
      data.append("body", values.body || firstFile?.name || "");
      data.append("userId", values.userId);
      data.append("queueId", values.queueId);
      data.append("medias", firstFile);
      await axios.request({ url: getEndpoint(), method: "POST", data, headers: { "Content-type": "multipart/form-data", Authorization: `Bearer ${values.token}` } });
      toast.success("Mensagem enviada com sucesso");
    } catch (err) { toastError(err); }
  };

  const handleSendTextoOnly = async (values) => {
    try {
      await axios.request({
        url: getEndpoint(), method: "POST",
        data: { number: values.number, body: values.body || " ", userId: values.userId, queueId: values.queueId },
        headers: { "Content-type": "application/json", Authorization: `Bearer ${values.token}` },
      });
      toast.success("Mensagem enviada com sucesso");
    } catch (err) { toastError(err); }
  };

  const handleSendBotoes = async (values) => {
    const buttons = [values.btn1, values.btn2, values.btn3].filter(Boolean).map((title, i) => ({ id: String(i + 1), title: title.substring(0, 20) }));
    if (!buttons.length) { toast.error("Preencha pelo menos um botão."); return; }
    try {
      await axios.request({
        url: getEndpoint(), method: "POST",
        data: { number: values.number, body: values.body || "Escolha uma opção:", buttons, userId: values.userId, queueId: values.queueId },
        headers: { "Content-type": "application/json", Authorization: `Bearer ${values.token}` },
      });
      toast.success("Mensagem enviada com sucesso");
    } catch (err) { toastError(err); }
  };

  const handleSendLista = async (values) => {
    let sections;
    try { sections = JSON.parse(values.sections || "[]"); } catch (_) {
      toast.error("Lista (sections): JSON inválido."); return;
    }
    if (!sections.length || !sections[0]?.rows?.length) {
      toast.error("Informe pelo menos uma seção com linhas."); return;
    }
    try {
      await axios.request({
        url: getEndpoint(), method: "POST",
        data: { number: values.number, body: values.body || "Selecione:", buttonText: values.buttonText || "Escolha uma opção", sections, userId: values.userId, queueId: values.queueId },
        headers: { "Content-type": "application/json", Authorization: `Bearer ${values.token}` },
      });
      toast.success("Mensagem enviada com sucesso");
    } catch (err) { toastError(err); }
  };

  const renderFormTesteTexto = () => (
    <Formik initialValues={formTextoData} enableReinitialize onSubmit={async (values, { setSubmitting }) => { await handleSendTextoOnly(values); setSubmitting(false); }}>
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12} md={6}><Field as={TextField} name="token" label="Token" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="number" label="Número" variant="outlined" size="small" fullWidth required placeholder="5585999999999" /></Grid>
            <Grid item xs={12}><Field as={TextField} name="body" label="Mensagem" variant="outlined" size="small" fullWidth multiline rows={3} required /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="userId" label="userId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="queueId" label="queueId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting} className={classes.submitBtn} startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                {isSubmitting ? "Enviando..." : "Enviar texto"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const renderFormTesteBotoes = () => (
    <Formik initialValues={formBotoesData} enableReinitialize onSubmit={async (values, { setSubmitting }) => { await handleSendBotoes(values); setSubmitting(false); }}>
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12} md={6}><Field as={TextField} name="token" label="Token" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="number" label="Número" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12}><Field as={TextField} name="body" label="Texto da mensagem" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={4}><Field as={TextField} name="btn1" label="Botão 1" variant="outlined" size="small" fullWidth inputProps={{ maxLength: 20 }} /></Grid>
            <Grid item xs={12} md={4}><Field as={TextField} name="btn2" label="Botão 2" variant="outlined" size="small" fullWidth inputProps={{ maxLength: 20 }} /></Grid>
            <Grid item xs={12} md={4}><Field as={TextField} name="btn3" label="Botão 3" variant="outlined" size="small" fullWidth inputProps={{ maxLength: 20 }} /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="userId" label="userId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="queueId" label="queueId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting} className={classes.submitBtn} startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                {isSubmitting ? "Enviando..." : "Enviar botões"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const renderFormTesteLista = () => (
    <Formik initialValues={formListaData} enableReinitialize onSubmit={async (values, { setSubmitting }) => { await handleSendLista(values); setSubmitting(false); }}>
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12} md={6}><Field as={TextField} name="token" label="Token" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="number" label="Número" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12}><Field as={TextField} name="body" label="Texto da mensagem" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}><Field as={TextField} name="buttonText" label="Texto do botão que abre a lista" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}><Field as={TextField} name="sections" label="Lista (sections) — JSON" variant="outlined" size="small" fullWidth multiline rows={6} /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="userId" label="userId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="queueId" label="queueId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting} className={classes.submitBtn} startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                {isSubmitting ? "Enviando..." : "Enviar lista"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const renderFormMessageMedia = () => (
    <Formik
      initialValues={formMessageMediaData}
      enableReinitialize
      onSubmit={async (values, actions) => {
        await handleSendMediaMessage(values);
        actions.setSubmitting(false);
        actions.resetForm();
        const el = document.getElementById("medias");
        if (el) { el.value = ""; }
      }}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12} md={6}><Field as={TextField} name="token" label="Token" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="number" label="Número" variant="outlined" size="small" fullWidth required /></Grid>
            <Grid item xs={12}><Field as={TextField} name="body" label="Legenda (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="userId" label="userId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12} md={6}><Field as={TextField} name="queueId" label="queueId (opcional)" variant="outlined" size="small" fullWidth /></Grid>
            <Grid item xs={12}>
              <div className={classes.fileInputWrap}>
                <input type="file" id="medias" className={classes.fileInput} onChange={(e) => setFile(e.target.files)} accept="image/*,video/*,audio/*,.pdf,.doc,.docx" />
                <label htmlFor="medias">
                  <Button variant="outlined" component="span" className={classes.fileButton}>Selecionar arquivo</Button>
                </label>
                {file?.[0] && <Typography variant="body2" color="textSecondary">{file[0].name}</Typography>}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting || !file?.[0]} className={classes.submitBtn} startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                {isSubmitting ? "Enviando..." : "Enviar mídia"}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  const base = getBaseApi();
  const modeloContent = [
    `POST ${getEndpoint()}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "number": "5585999999999",\n  "body": "Olá! Esta é uma mensagem de texto.",\n  "userId": "",\n  "queueId": ""\n}`,
    `POST ${getEndpoint()}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: multipart/form-data\n\nFormData:\n  number: "5585999999999"\n  body: "Legenda"\n  userId: ""\n  queueId: ""\n  medias: [arquivo]`,
    `POST ${getEndpoint()}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "number": "5585999999999",\n  "body": "Escolha uma opção:",\n  "buttons": [{"title":"Sim"},{"title":"Não"},{"title":"Talvez"}],\n  "userId": "",\n  "queueId": ""\n}\n\n(Máx. 3 botões; título até 20 caracteres)`,
    `POST ${getEndpoint()}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "number": "5585999999999",\n  "body": "Selecione:",\n  "buttonText": "Escolha uma opção",\n  "sections": [{"title":"Opções","rows":[{"id":"1","title":"Opção A"},{"id":"2","title":"Opção B"}]}],\n  "userId": "",\n  "queueId": ""\n}\n\n(Até 10 itens; título da linha até 24 caracteres)`,
  ];

  const ticketContent = [
    `GET ${base}/tickets/{ticketId}\nAuthorization: Bearer SEU_TOKEN\n\nRetorna o ticket (status, queueId, userId, contactId, …).\nTambém aceita UUID no path.`,
    `PUT ${base}/tickets/{ticketId}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "status": "open",\n  "userId": 12,\n  "queueId": 1\n}\n\n# Handoff bot → humano: status open + userId\n# Voltar à fila/bot: { "status": "pending", "userId": null, "queueId": 1 }\n# Encerrar: { "status": "closed", "sendFarewellMessage": false }`,
    `POST ${getEndpoint()}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "number": "5585999999999",\n  "body": "Vou te transferir para um atendente.",\n  "userId": 12,\n  "queueId": 1\n}\n\n# Atalhos no envio (mesmo token):\n# • só queueId → ticket pending na fila\n# • userId → ticket open + atendente\n# • closeTicket: true → fecha após enviar`,
    `GET ${base}/list/{ticketId}\nAuthorization: Bearer SEU_TOKEN\n\nHistórico de mensagens do ticket (útil para o bot/LLM).\nResposta inclui messages[] e text linear.`,
  ];

  const contatoContent = [
    `GET ${base}/contacts/{contactId}\nAuthorization: Bearer SEU_TOKEN\n\nRetorna o contato com situacao, canal, regiao e customDynamicFields.`,
    `PUT ${base}/contacts/{contactId}\nAuthorization: Bearer SEU_TOKEN\nContent-Type: application/json\n\n{\n  "situacao": "QUALIFICADO",\n  "canal": "WHATSAPP",\n  "regiao": "SUL",\n  "customDynamicFields": {\n    "PLANO": "Premium",\n    "INTERESSE": ["Implante", "Ortodontia"]\n  }\n}\n\n# Usa o mesmo UpdateContactService do CRM.`,
    `GET ${base}/contact-field-groups\nAuthorization: Bearer SEU_TOKEN\n\nLista grupos/definição dos campos (param, label, fieldKind).\nCadastro visual: menu Campos personalizados.`,
  ];

  return (
    <Paper ref={paperRef} className={classes.root} variant="outlined">
      <Box className={classes.header}>
        <Typography className={classes.title}>{i18n.t("messagesAPI.API.title")}</Typography>
        <Typography className={classes.subtitle}>
          Mensagens, ticket (status/handoff) e campos customizados — tudo com o token da conexão WhatsApp.
        </Typography>
      </Box>

      <Card className={classes.card}>
        <CardHeader
          avatar={<VpnKeyIcon color="primary" />}
          title="Autenticação (ConnectionToken)"
          subheader="Um único Bearer para envio, ticket e contato"
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Typography variant="body2" component="ul" className={classes.instructionsList}>
            <li>
              Token: menu <strong>Conexões</strong> → editar a conexão WhatsApp → campo <strong>Token</strong> (copie).
            </li>
            <li>
              Header em todas as rotas: <code>Authorization: Bearer SEU_TOKEN</code>
            </li>
            <li>
              Prefixo da API: <code>{base}</code>
            </li>
            <li>
              Não é a API do Chatwoot. Clique Responde usa ticket/contato (não conversation/inbox).
            </li>
          </Typography>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          avatar={<CodeIcon color="primary" />}
          title="Modelos de envio"
          subheader="Exemplos de payload para cada tipo de mensagem. Copie e adapte nas suas integrações."
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Tabs value={tabModelo} onChange={(_, v) => setTabModelo(v)} className={classes.tabsRoot} indicatorColor="primary" textColor="primary">
            <Tab label="Texto" className={classes.tab} />
            <Tab label="Mídia" className={classes.tab} />
            <Tab label="Botões" className={classes.tab} />
            <Tab label="Lista" className={classes.tab} />
          </Tabs>
          <Box pt={2}>
            <pre className={classes.codeBlock}>{modeloContent[tabModelo]}</pre>
          </Box>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          avatar={<ConfirmationNumberIcon color="primary" />}
          title="Ticket — status e handoff"
          subheader="Mesmo token do envio. Delega UpdateTicketService / ShowTicketService do CRM."
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Tabs value={tabTicket} onChange={(_, v) => setTabTicket(v)} className={classes.tabsRoot} indicatorColor="primary" textColor="primary">
            <Tab label="Ver ticket" className={classes.tab} />
            <Tab label="Alterar status" className={classes.tab} />
            <Tab label="Handoff no send" className={classes.tab} />
            <Tab label="Histórico" className={classes.tab} />
          </Tabs>
          <Box pt={2}>
            <pre className={classes.codeBlock}>{ticketContent[tabTicket]}</pre>
          </Box>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          avatar={<PersonIcon color="primary" />}
          title="Contato — campos customizados"
          subheader="Mesmo token. Delega UpdateContactService / ListContactFieldGroupsService."
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Tabs value={tabContato} onChange={(_, v) => setTabContato(v)} className={classes.tabsRoot} indicatorColor="primary" textColor="primary">
            <Tab label="Ver contato" className={classes.tab} />
            <Tab label="Atualizar campos" className={classes.tab} />
            <Tab label="Grupos de campo" className={classes.tab} />
          </Tabs>
          <Box pt={2}>
            <pre className={classes.codeBlock}>{contatoContent[tabContato]}</pre>
          </Box>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          avatar={<DescriptionIcon color="primary" />}
          title={i18n.t("messagesAPI.API.instructions.title")}
          subheader="Requisitos para envio"
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Typography variant="body2" component="ul" className={classes.instructionsList}>
            <li>{i18n.t("messagesAPI.API.instructions.comments1")}</li>
            <li>
              {i18n.t("messagesAPI.API.instructions.comments2")}
              <ul className={classes.instructionsList}>
                <li>{i18n.t("messagesAPI.API.instructions.codeCountry")}</li>
                <li>{i18n.t("messagesAPI.API.instructions.code")}</li>
                <li>{i18n.t("messagesAPI.API.instructions.number")}</li>
              </ul>
            </li>
          </Typography>
        </CardContent>
      </Card>

      <Card className={classes.card}>
        <CardHeader
          avatar={<SendIcon color="primary" />}
          title="Teste de envio"
          subheader="Preencha os campos e envie para testar cada tipo de mensagem."
          className={classes.cardHeader}
        />
        <CardContent className={classes.cardContent}>
          <Tabs value={tabTeste} onChange={(_, v) => setTabTeste(v)} className={classes.tabsRoot} indicatorColor="primary" textColor="primary">
            <Tab label="Texto" className={classes.tab} />
            <Tab label="Mídia" className={classes.tab} />
            <Tab label="Botões" className={classes.tab} />
            <Tab label="Lista" className={classes.tab} />
          </Tabs>
          <Divider className={classes.divider} />
          <Box pt={1}>
            {tabTeste === 0 && renderFormTesteTexto()}
            {tabTeste === 1 && renderFormMessageMedia()}
            {tabTeste === 2 && renderFormTesteBotoes()}
            {tabTeste === 3 && renderFormTesteLista()}
          </Box>
        </CardContent>
      </Card>
    </Paper>
  );
};

export default MessagesAPI;
